import * as turf from '@turf/turf';
import Collection from 'ol/Collection';
import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import { Modify, Translate } from 'ol/interaction';
import { Vector as VectorLayer } from 'ol/layer';
import { fromLonLat, toLonLat } from 'ol/proj';
import { Vector as VectorSource } from 'ol/source';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import actions from '@/actions';
import { SURVEY_AREA_MAX } from '@/config';
import { STYLES } from '@/define/olMaps';
import OlMap from '@/helpers/OlMap';
import { NotifierService as notifier } from '@/libs/Notifier';
import CoordUtils from '@/utils/CoordUtils';
import { getPositions } from '@/utils/SurveyCalculator';

const Group = ({ index, data }) => {
  const dispatch = useDispatch();
  const map = OlMap.getMap();

  const originCoords = useMemo(() => {
    return data.boundary.map(({ lat, lng }) => fromLonLat([lng, lat]));
  }, [data.boundary]);

  useEffect(() => {
    const feature = new Feature(new Polygon([originCoords]));
    const source = new VectorSource({ features: [feature], wrapX: false });
    const layer = new VectorLayer({
      source,
      style: STYLES.SURVEY,
    });
    map.addLayer(layer);

    const translate = new Translate({ features: new Collection([feature]) });
    translate.on('translateend', editShape);
    map.addInteraction(translate);

    const modify = new Modify({ source });
    modify.on('modifyend', editShape);
    map.addInteraction(modify);

    return () => {
      source.clear();
      map.removeLayer(layer);
      map.removeInteraction(modify);
      map.removeInteraction(translate);
    };
  }, [originCoords]);

  const editShape = (e) => {
    if (data.segment) {
      handleInvalidAction(e, 'Please deactivate the segment option to edit the area.');
      return;
    }

    const coords = e.features.item(0).getGeometry().getCoordinates()[0];
    const currShape = turf.polygon([coords.map((coord) => toLonLat(coord))]);
    const prevShape = turf.polygon([originCoords.map((coord) => toLonLat(coord))]);

    // 동일한 모양인 경우
    if (turf.booleanEqual(currShape, prevShape)) return;

    const selfIntersections = turf.kinks(currShape).features;
    // 자기교차점 존재 시
    if (selfIntersections.length > 0) {
      handleInvalidAction(e, 'The edited area cannot have self-intersections. Please retry.');
      return;
    }

    const area = turf.area(currShape);
    // 면적 200km² 초과 시
    if (area > SURVEY_AREA_MAX) {
      handleInvalidAction(e, `The edited area cannot exceed ${SURVEY_AREA_MAX / (10 ^ 6)}km². Please retry.`);
      return;
    }

    const boundary = coords.map((coord) => {
      return CoordUtils.objectFromArray(toLonLat(coord));
    });
    const options = {
      entryIndex: data.entryIndex,
      gap: data.gap,
      turnaround: data.turnaround,
      rotate: data.rotate,
      camera: data.camera,
    };
    const positions = getPositions(boundary, options);
    // 경로점 미존재 시
    if (positions.length === 0) {
      handleInvalidAction(e, 'The edited area is too small.');
      return;
    }

    dispatch(actions.mission.editSurvey(index, { boundary, positions }));
  };

  const handleInvalidAction = (e, message) => {
    e.features.item(0).getGeometry().setCoordinates([originCoords]);
    notifier.error(message);
  };

  return null;
};

export default Group;
